// TravelPreferencesPage.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Checkbox, FormControl, FormControlLabel,
    InputLabel, MenuItem, Select, TextField, Typography,
    ThemeProvider, createTheme, IconButton, Popover
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/he';

import { Icons } from '../data/Icons';
import {
    styleOptions,
    regionOptions,
    difficultyOptions,
    durationOptions,
    groupSizeOptions
} from '../data/TravelOptions';

import './TravelPreferencesPage.css';

import { saveSingleUserTrip } from '../firebase/SingleUserTrips';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import DatePickerCalendar from "./DatePickerCalendar";
import Swal from 'sweetalert2';

dayjs.locale('he');

const theme = createTheme({
    direction: 'rtl',
    typography: {
        fontFamily: 'Varela Round, sans-serif',
    },
});

const TravelPreferencesPage = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({});
    const [dates, setDates] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [errors, setErrors] = useState({ dates: false });

    useEffect(() => {
        if (isOpen) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            // ✅ איפוס השדות עם פתיחת הפופאפ
            setFormData({});
            setDates([]);
            setErrors({ dates: false });
        } else {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleDatesChange = (newDates) => {
        setDates(newDates);
        if (newDates.length > 0 && errors.dates) {
            setErrors({ ...errors, dates: false });
        }
    };

    const handleDateFieldClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const menuProps = {
        PaperProps: {
            style: {
                maxHeight: 200,
                overflowY: 'auto',
                zIndex: 1302
            },
            dir: 'rtl'
        },
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'right' }
    };

    // ✅ הפונקציה המעודכנת
    const handleSubmit = async () => {
        if (dates.length === 0) {
            setErrors({ dates: true });
            return;
        } else {
            setErrors({ dates: false });
        }
        try {
            const uid = localStorage.getItem("uid");
            const userDoc = await getDoc(doc(getFirestore(), "users", uid));
            const userProfile = userDoc.data();

            const tripPreferences = {
                ...formData,
                dates: dates.map(date => new Date(date).toISOString())
            };

            await saveSingleUserTrip(userProfile, tripPreferences);
            setFormData({});
            setDates([]);
            Swal.fire({
                icon: 'success',
                title: 'הטיול נוצר בהצלחה!',
                text: 'הטיול יוצג כעת למטיילים אחרים וניתן לעקוב אחריו בעמוד הטיולים שלי 😊',
                confirmButtonText: 'מעולה!',
                customClass: {
                    popup: 'swal2-popup',
                    confirmButton: 'swal2-confirm-green'
                }
            });
        } catch (error) {
            console.error("שגיאה בשמירת הטיול:", error);
        }

        onClose();
    };

    return (
        <ThemeProvider theme={theme}>
            <Box className="modal-overlay-profile">
                <Box className="modal-content-profile">
                    <button className="close-button-profile" onClick={onClose}>✖</button>
                    <Typography variant="h5" className="edit-profile-heading">
                        בחירת מאפייני הטיול שלי
                    </Typography>
                    <Typography variant="body2" className="popup-description">
                        מלא/י את הפרטים על סגנון הטיול שמתאים לך – והוא יוצג למטיילים אחרים שמחפשים שותפים.
                        אם מישהו יתאים, תוכלו לצאת יחד להרפתקה!
                    </Typography>
                    <DatePickerCalendar
                        multiple
                        value={dates}
                        onChange={handleDatesChange}
                        label={`${Icons.date} תאריכים נבחרים`}
                        required
                        error={errors.dates}
                        helperText={errors.dates ? <span style={{display:'block',textAlign:'right',width:'100%'}}>יש לבחור תאריך אחד לפחות</span> : ''}
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="style-label">{Icons.style} סגנון טיול</InputLabel>
                        <Select
                            labelId="style-label"
                            multiple
                            value={formData.style || []}
                            onChange={(e) => handleChange('style', e.target.value)}
                            inputProps={{ dir: 'rtl' }}
                            MenuProps={menuProps}
                        >
                            {styleOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="region-label">{Icons.location} אזור טיול</InputLabel>
                        <Select
                            labelId="region-label"
                            multiple
                            value={formData.region || []}
                            onChange={(e) => handleChange('region', e.target.value)}
                            inputProps={{ dir: 'rtl' }}
                            MenuProps={menuProps}
                        >
                            {regionOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="difficulty-label">{Icons.difficulty} רמת קושי</InputLabel>
                        <Select
                            labelId="difficulty-label"
                            multiple
                            value={formData.difficulty || []}
                            onChange={(e) => handleChange('difficulty', e.target.value)}
                            inputProps={{ dir: 'rtl' }}
                            MenuProps={menuProps}
                        >
                            {difficultyOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="duration-label">{Icons.duration} משך הטיול</InputLabel>
                        <Select
                            labelId="duration-label"
                            multiple
                            value={formData.duration || []}
                            onChange={(e) => handleChange('duration', e.target.value)}
                            inputProps={{ dir: 'rtl' }}
                            MenuProps={menuProps}
                        >
                            {durationOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="groupSize-label">{Icons.groupSize} גודל הקבוצה</InputLabel>
                        <Select
                            labelId="groupSize-label"
                            multiple
                            value={formData.groupSize || []}
                            onChange={(e) => handleChange('groupSize', e.target.value)}
                            inputProps={{ dir: 'rtl' }}
                            MenuProps={menuProps}
                        >
                            {groupSizeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* <Box sx={{ mt: 3 }}>
                        <FormControlLabel
                            control={<Checkbox onChange={(e) => handleChange('accessible', e.target.checked)} />}
                            label="אני מעוניינ/ת במסלול נגיש ♿"
                        />
                        <FormControlLabel
                            control={<Checkbox onChange={(e) => handleChange('attractions', e.target.checked)} />}
                            label="אני מעוניינ/ת לטייל רק עם אנשים מהמגדר שלי 🚻"
                        />
                    </Box> */}

                    <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 3, backgroundColor: "#1B5C1C" }}
                        onClick={handleSubmit}
                    >
                        בואו נחפש שותפים
                    </Button>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default TravelPreferencesPage;
